import dayjs from 'dayjs'
import { SaveManualActionPlanBody } from '@/service/actionPlans/body'
import { EvidencesTypeEnum, EvidencesTypeValueEnum } from '@/utils/enums/evidences'
import { EvidenceData } from '@/hooks/evidences/typyng'
import { ManualActionPlanData } from './types'

const EVIDENCE_TYPE_TO_ID: Record<string, number> = {
  [EvidencesTypeEnum.IMAGE]: EvidencesTypeValueEnum.IMAGE,
  [EvidencesTypeEnum.VIDEO]: EvidencesTypeValueEnum.VIDEO,
  [EvidencesTypeEnum.SIGN]: EvidencesTypeValueEnum.SIGN,
  [EvidencesTypeEnum.FILE]: EvidencesTypeValueEnum.DOCUMENT,
  [EvidencesTypeEnum.COMMENT]: EvidencesTypeValueEnum.COMMENT,
  [EvidencesTypeEnum.CAMERA]: EvidencesTypeValueEnum.CAMERA,
  [EvidencesTypeEnum.VIDEO_GALLERY]: EvidencesTypeValueEnum.VIDEO_GALLERY,
}

const CHECK_FORMATS = ['png', 'jpg', 'jpeg', 'mp4', 'wmv', 'wma', 'pdf', 'csv', 'docx', 'xlsx', 'pptx', 'txt']

const getCheckedTypes = (values: Record<string, unknown> | undefined): string[] => {
  if (!values || typeof values !== 'object') return []
  return CHECK_FORMATS.filter((format) => !!values[format])
}

const getNumberValue = (value: unknown): number => {
  if (typeof value === 'number') return value
  if (typeof value === 'string') return Number(value) || 0
  return 0
}

const getSizeValue = (maxSize: unknown): number => {
  if (maxSize && typeof maxSize === 'object' && 'value' in maxSize) {
    return getNumberValue((maxSize as { value?: unknown }).value)
  }
  return 0
}

export const mapSolverEvidences = (
  solverEvidences: EvidenceData[]
): SaveManualActionPlanBody['solverEvidences'] => {
  const firstItem = solverEvidences[0]
  if (!firstItem) return []

  return Object.entries(firstItem)
    .filter(([, values]) => !!values?.active)
    .map(([key, values]) => {
      const evidenceTypeId = EVIDENCE_TYPE_TO_ID[key]

      return {
        evidenceTypeId: evidenceTypeId ?? 0,
        types: getCheckedTypes(values as Record<string, unknown>),
        sizes: getSizeValue(values?.maxSize),
        amounts: getNumberValue(values?.maxAmount),
        required: !!values?.required,
        description: '',
      }
    })
}

export const buildManualActionPlanPayload = (
  data: ManualActionPlanData,
  t: (key: string) => string
): SaveManualActionPlanBody => {
  const comments = data.evidences.flatMap((evidence) => evidence.comments)

  return {
    templateName: data.title,
    description: data.instructions,
    finishDate: data.endDate ? dayjs(data.endDate).format('YYYY-MM-DDTHH:mm:ss') : '',
    userAssignId: data.assignedUser?.id ?? 0,
    questionnaireId: data.linkQuestionnaire.questionnaireId,
    questionIds: data.linkQuestionnaire.questionIds,
    items: data.items
      .filter((item) => item.selected)
      .map((item) => ({
        question: t(item.label),
        required: 0,
        agentAnswer: item.answer,
      })),
    sendEmail: data.sendEmailCopy,
    evidences: data.evidences.flatMap((evidence) =>
      evidence.files.map((file) => ({
        evidenceTypeId: file.evidenceTypeId,
        originalname: file.name,
        url: file.url,
      }))
    ),
    solverEvidences: mapSolverEvidences(data.solverEvidences),
    comments,
    usersApprovers: data.approvers.map((user) => user.id),
    usersNotified: {
      users: data.selectedInternalUsers,
      emailsExternal: data.externalEmails,
    },
  }
}
